/**
 * weaponVfx.ts — BETA 22.70 (Weapon Feel Beta B): per-weapon ATTACK visuals.
 *
 * The layered-VFX registry from Dylan's brief: every weapon's attack is built
 * from kind-keyed layers + an escape hatch for true one-off draws (flame
 * cones, rail lines). A weapon with NO entry renders exactly like before —
 * zero regression until it opts in.
 *
 * LEAF MODULE — imports nothing from weaponSystem/engine (no cycles).
 * weaponSystem.ts calls into it from the projectile render dispatch.
 *
 * ── VFX SPEC — FIRESTARTER (fireball_wand) ─────────────────────────────────
 * Weapon: Firestarter (flamethrower)
 * What the icon shows: red extinguisher tank rebuilt into a flamethrower,
 *   small pilot flame at the nozzle.
 * Projectile/attack shape: a GOUT of layered flame tongues — dark red base,
 *   orange mid, yellow-white hot core — no orb, no circle.
 * Movement: fast straight jet (existing projectile physics untouched).
 * Trail: the tongues themselves lick backward; ember specks shed in flight
 *   (default dot trail DISABLED — it read as circles).
 * Impact: ember burst via the generic impact bridge + enemy IGNITES.
 * Aftermath/decal: charred scorch blotches bake onto the floor (~1 in 3 hits).
 * Shadow: none — fire is light, not a physical prop.
 * Recoil: tiny (2wu) — it's a hose, not a cannon (set in weaponVisualConfig).
 * Hit pause / camera shake: none — continuous weapon, pause would stutter.
 * Enemy reaction: burnUntil ember-bed + flame tongues on the victim for the
 *   full burn window (the taco-fire render in engine.ts) — and the burn is
 *   REAL since 22.74: 6 dmg/s for 2s, halved vs elites/bosses (Dylan-approved
 *   2026-07-02), ticking through the shared enemy burn engine.
 * What makes it funny/satisfying: a fire extinguisher committing arson;
 *   enemies run around visibly on fire.
 * What would make it look cheap: a flat orange ball (the old look), clean
 *   gradient cones, circle trails.
 * ───────────────────────────────────────────────────────────────────────────
 */

/** Minimal structural view of a projectile (avoids importing weaponSystem). */
export interface ProjectileForVfx {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  age: number;
  size: number;
  isCrit: boolean;
}

export interface WeaponVfxSpec {
  /** Replaces the entire default projectile body render (shape+glow+sprite). */
  drawBody?: (
    ctx: CanvasRenderingContext2D,
    proj: ProjectileForVfx,
    screenX: number,
    screenY: number,
    zoom: number,
  ) => void;
  /** Suppress the default trailPositions render for this weapon. */
  suppressDefaultTrail?: boolean;
  /** Seconds of burn applied to enemies hit by this weapon (0 = none).
   *  Drives the burning-enemy ember render (burnUntil); with burnDps set it
   *  is also the REAL DoT duration. */
  burnVisualSec?: number;
  /** BETA 22.74 — real burn damage/sec while burning (Dylan-approved:
   *  flamethrower 6 dps for 2s, halved vs elites/bosses in the adapter).
   *  Rides the existing enemy burn tick (0.5s cadence). Absent = visual-only. */
  burnDps?: number;
  /** Ground decal stamped at hit points. */
  hitDecal?: { kind: 'scorch' | 'splat' | 'foam' | 'smear' | 'frost'; chance: number; size: [number, number] };
  /** BETA 22.73 — trail identity (was hardcoded weapon-id lists in the
   *  weaponSystem trail renderer): 'streak' = tapered velocity line,
   *  'streakCore' = streak + white-hot core (revolver tracer),
   *  'ghostHeavy' = dense slow-shrink sprite afterimages (cannonball).
   *  Absent = automatic (boomerang return streak / sprite ghost / dot). */
  trailKind?: 'streak' | 'streakCore' | 'ghostHeavy';
  /** BETA 22.74 — melee contact-spark color at the visual hit point (VFX
   *  identity law: every melee weapon gets its OWN read; the old renderer
   *  used one gray for all). Absent = legacy dirty-gray '#c8c0b0'. */
  meleeSparkColor?: string;
}

/** Particle-spawner bridge shape (mirrors weaponSystem's registered spawner). */
export type VfxParticleSpawner = (
  x: number, y: number, color: string, count: number,
  opts?: Record<string, number | string | boolean>,
) => void;

/**
 * BETA 22.73 — per-weapon FIRE SIGNATURE (the muzzle-moment particles).
 * Moved OUT of weaponSystem's fireProjectile switch so every weapon-id
 * VFX decision lives in this module. `isFirstShot` = first pellet of a
 * burst with no fan offset (the old currentAmmo===projectileCount &&
 * extraAngleDeg===0 gate).
 */
export function spawnFireSignature(
  weaponId: string,
  spawn: VfxParticleSpawner,
  x: number,
  y: number,
  angle: number,
  isFirstShot: boolean,
): void {
  switch (weaponId) {
    case 'revolver':
      // Clean single shot: tiny radial GOLD speckle ring (360°, no cone).
      spawn(x, y, '#FFD700', 6, { shape: 'spark', speedMin: 80, speedMax: 220, lifeMin: 0.12, lifeMax: 0.28, sizeMin: 1.5, sizeMax: 3, stretch: 3 });
      break;
    case 'rpk_machine_gun':
      // Full-auto STREAM: 3-spark ORANGE cascade in the firing cone.
      spawn(x, y, '#FF7A1A', 3, { shape: 'spark', dir: angle, spread: 0.5, speedMin: 140, speedMax: 320, lifeMin: 0.1, lifeMax: 0.22, sizeMin: 1.5, sizeMax: 3, stretch: 5 });
      break;
    case 'blunderbuss':
      // Black-powder smoke RISES from the barrel, once per burst.
      if (isFirstShot) {
        spawn(x, y, '#9a9a9a', 7, { shape: 'mote', gravity: -60, drag: 0.9, speedMin: 15, speedMax: 60, lifeMin: 0.5, lifeMax: 0.9, sizeMin: 2, sizeMax: 4 });
      }
      break;
    case 'wpn_karen_drones':
      // Gold star-glint at the DRONE muzzle when it throws.
      if (isFirstShot) {
        spawn(x, y, '#FFD700', 4, { shape: 'star', spin: 6, spinRandomSign: true, speedMin: 30, speedMax: 110, lifeMin: 0.25, lifeMax: 0.45, sizeMin: 2, sizeMax: 4 });
      }
      break;
    case 'wpn_ex_girlfriend':
      // Pink sparkle burst on the FIRST shot of the fan.
      if (isFirstShot) {
        spawn(x, y, '#ff66cc', 5, { shape: 'star', spin: 7, spinRandomSign: true, speedMin: 40, speedMax: 140, lifeMin: 0.3, lifeMax: 0.55, sizeMin: 2, sizeMax: 4 });
      }
      break;
    // ── BETA 22.76 — muzzle signatures for weapons that fired with only the
    // generic 3-ring flash (22.75 audit).
    case 'peanut_rifle':
      // .50 Cal: gray muzzle-smoke puff + a fast forward shock-streak — a
      // heavy high-caliber blast, not a pop.
      spawn(x, y, '#b8b8b8', 5, { shape: 'mote', dir: angle, spread: 0.35, speedMin: 40, speedMax: 130, lifeMin: 0.25, lifeMax: 0.5, sizeMin: 2, sizeMax: 4, drag: 0.9 });
      spawn(x, y, '#fff2c0', 3, { shape: 'spark', dir: angle, spread: 0.12, speedMin: 260, speedMax: 460, lifeMin: 0.08, lifeMax: 0.16, sizeMin: 1.5, sizeMax: 3, stretch: 7 });
      break;
    case 'wpn_smg':
      // The Intern: tiny brass casing kicked sideways + a wisp of smoke.
      spawn(x, y, '#c9b98a', 2, { shape: 'spark', dir: angle + Math.PI * 0.5, spread: 0.6, speedMin: 60, speedMax: 160, lifeMin: 0.2, lifeMax: 0.4, sizeMin: 1, sizeMax: 2, gravity: 220, spin: 8, spinRandomSign: true });
      spawn(x, y, '#9a9a9a', 2, { shape: 'mote', dir: angle, spread: 0.4, speedMin: 20, speedMax: 70, lifeMin: 0.15, lifeMax: 0.3, sizeMin: 1.5, sizeMax: 2.5 });
      break;
    case 'wpn_frozen_darts':
      // Brain Freeze: a puff of frost mist off the nozzle.
      spawn(x, y, '#cfeeff', 5, { shape: 'mote', dir: angle, spread: 0.5, speedMin: 25, speedMax: 90, lifeMin: 0.25, lifeMax: 0.5, sizeMin: 2, sizeMax: 4, drag: 0.92 });
      break;
    case 'wpn_railgun':
      // Railgun: electric-blue discharge crackle at the muzzle on fire (the
      // charge wind-up glow is emitted separately in weaponSystem).
      spawn(x, y, '#7fc4ff', 7, { shape: 'spark', dir: angle, spread: 0.7, speedMin: 120, speedMax: 340, lifeMin: 0.1, lifeMax: 0.25, sizeMin: 1.5, sizeMax: 3.5, stretch: 5 });
      spawn(x, y, '#eaf4ff', 3, { shape: 'spark', dir: angle, spread: 0.2, speedMin: 200, speedMax: 420, lifeMin: 0.08, lifeMax: 0.16, sizeMin: 1.5, sizeMax: 3, stretch: 8 });
      break;
    case 'wpn_squirter':
      // Squirter: a light spray of water droplets at the nozzle.
      spawn(x, y, '#aaddff', 3, { shape: 'bubble', dir: angle, spread: 0.4, speedMin: 30, speedMax: 110, lifeMin: 0.15, lifeMax: 0.35, sizeMin: 1.5, sizeMax: 3 });
      break;
    case 'wpn_ugly_kittens':
      // Cat Cannon: a puff of fur off the spawn.
      spawn(x, y, '#d8c8a8', 5, { shape: 'mote', speedMin: 20, speedMax: 80, lifeMin: 0.3, lifeMax: 0.6, sizeMin: 2, sizeMax: 4, gravity: -20, drag: 0.9 });
      break;
  }
}

/**
 * Continuous flame CONE at the Firestarter's nozzle — drawn EVERY frame the
 * weapon has a target. BETA 22.72: length + spread now MATCH the damage cone
 * (what burns is exactly what you see) and the shape is built from many
 * two-octave flickering edge samples instead of three clean polygons
 * (Dylan: "looks a little fake"). Deterministic — no RNG in the render.
 */
export function drawFlameCone(
  ctx: CanvasRenderingContext2D,
  mx: number,
  my: number,
  angle: number,
  zoom: number,
  time: number,
  lenWu: number,
  halfAngle: number,
): void {
  const t1 = time * 17;
  const t2 = time * 31;                       // second flicker octave — kills the "geometric" read
  const breathe = 0.88 + Math.sin(time * 8) * 0.07 + Math.sin(time * 21) * 0.05;
  const len = lenWu * zoom * breathe;
  const endW = Math.tan(halfAngle) * len;     // cone half-width AT THE TIP = the real hitbox edge
  ctx.save();
  ctx.translate(mx, my);
  ctx.rotate(angle);
  ctx.globalCompositeOperation = 'lighter';

  // Each layer = a ragged cone built from many edge samples with per-sample
  // two-octave flicker. Outer layers are dim + full length; the core is
  // short, narrow and hot — the classic flamethrower color ramp.
  const layers: Array<[string, number, number, number]> = [
    ['rgba(110,26,0,0.34)', 1.0, 1.0, 0],
    ['rgba(200,74,4,0.4)', 0.92, 0.74, 2.1],
    ['rgba(255,150,30,0.42)', 0.72, 0.5, 4.2],
    ['rgba(255,224,130,0.5)', 0.42, 0.3, 6.3],
  ];
  const N = 7;                                 // edge samples per side
  for (const [color, lm, wm, phase] of layers) {
    const L = len * lm;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    for (let i = 1; i <= N; i++) {
      const f = i / N;
      const w = endW * wm * f * (0.75 + 0.25 * Math.sin(t1 + phase + i * 2.3))
        + Math.sin(t2 + phase * 1.7 + i * 3.1) * endW * 0.14 * f;
      ctx.lineTo(L * f + Math.sin(t2 + i) * len * 0.015, -w);
    }
    // ragged licking tip
    ctx.lineTo(L * (1.02 + Math.sin(t1 * 1.3 + phase) * 0.05), 0);
    for (let i = N; i >= 1; i--) {
      const f = i / N;
      const w = endW * wm * f * (0.75 + 0.25 * Math.cos(t1 * 1.1 + phase + i * 2.7))
        + Math.cos(t2 * 0.9 + phase + i * 2.9) * endW * 0.14 * f;
      ctx.lineTo(L * f + Math.cos(t2 + i * 1.4) * len * 0.015, w);
    }
    ctx.closePath();
    ctx.fill();
  }
  // white-hot jet right at the nozzle
  ctx.fillStyle = 'rgba(255,246,214,0.85)';
  ctx.beginPath();
  ctx.ellipse(len * 0.07, 0, len * 0.07, endW * 0.13 + 1.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// ── BETA 22.76 — custom projectile BODIES (22.75 audit: these weapons were on
// generic recolored shapes). Each replaces the entire default body render (the
// caller wraps save/restore). ProjectileForVfx has no id, so all motion is
// derived from age + velocity (deterministic). Only weapons with an EMPTY
// projectile.spriteUrl use these — sprite projectiles stay untouched (sacred).

/** RPG-7 — an oriented rocket: dark body, warhead nose, tail fins, exhaust,
 *  gentle in-flight wobble. Was the shared BETA-20.69 comet orb. */
function drawRpgRocket(ctx: CanvasRenderingContext2D, proj: ProjectileForVfx, sx: number, sy: number, zoom: number): void {
  const ang = Math.atan2(proj.velocityY, proj.velocityX);
  const L = Math.max(11, proj.size * zoom * 2.6);
  const W = Math.max(3, proj.size * zoom * 0.85);
  ctx.translate(sx, sy);
  ctx.rotate(ang);
  ctx.translate(0, Math.sin(proj.age * 20) * W * 0.3);   // wobble
  // exhaust flame (behind the tail), flickering length
  const flame = L * (0.55 + 0.35 * Math.sin(proj.age * 40));
  ctx.fillStyle = 'rgba(255,150,40,0.8)';
  ctx.beginPath();
  ctx.moveTo(-L * 0.5, -W * 0.55); ctx.lineTo(-L * 0.5 - flame, 0); ctx.lineTo(-L * 0.5, W * 0.55); ctx.closePath(); ctx.fill();
  ctx.fillStyle = 'rgba(255,240,180,0.9)';
  ctx.beginPath();
  ctx.moveTo(-L * 0.5, -W * 0.3); ctx.lineTo(-L * 0.5 - flame * 0.55, 0); ctx.lineTo(-L * 0.5, W * 0.3); ctx.closePath(); ctx.fill();
  // tail fins
  ctx.fillStyle = '#2a2a2a';
  ctx.beginPath(); ctx.moveTo(-L * 0.5, -W); ctx.lineTo(-L * 0.5 - W, -W * 1.8); ctx.lineTo(-L * 0.3, -W); ctx.closePath(); ctx.fill();
  ctx.beginPath(); ctx.moveTo(-L * 0.5, W); ctx.lineTo(-L * 0.5 - W, W * 1.8); ctx.lineTo(-L * 0.3, W); ctx.closePath(); ctx.fill();
  // dark cylindrical body
  ctx.fillStyle = '#3a3a3a';
  ctx.fillRect(-L * 0.5, -W, L, W * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.15)';           // top highlight
  ctx.fillRect(-L * 0.5, -W, L, W * 0.5);
  // warhead nose cone (front)
  ctx.fillStyle = proj.isCrit ? '#ff5533' : '#b0402a';
  ctx.beginPath(); ctx.moveTo(L * 0.5, -W); ctx.lineTo(L * 0.5 + W * 1.7, 0); ctx.lineTo(L * 0.5, W); ctx.closePath(); ctx.fill();
}

/** Sawed-Off pellet — a small hot lead ball: dark rim + offset gold hotspot
 *  (NOT the RPK-clone white-center circle the old comment lied about). */
function drawBlunderbussPellet(ctx: CanvasRenderingContext2D, proj: ProjectileForVfx, sx: number, sy: number, zoom: number): void {
  const r = Math.max(2, proj.size * zoom * 1.1);
  ctx.fillStyle = proj.isCrit ? '#c07a2e' : '#5a4a2a';
  ctx.beginPath(); ctx.arc(sx, sy, r, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#FFD700';
  ctx.beginPath(); ctx.arc(sx - r * 0.28, sy - r * 0.28, r * 0.5, 0, Math.PI * 2); ctx.fill();
}

/** The Ex Girlfriend — a pink heart with a jagged white crack down it, tip
 *  forward along travel. Dylan: "broken-heart shots, NOT plain pink bullets." */
function drawBrokenHeart(ctx: CanvasRenderingContext2D, proj: ProjectileForVfx, sx: number, sy: number, zoom: number): void {
  const s = Math.max(3, proj.size * zoom * 1.7);
  const ang = Math.atan2(proj.velocityY, proj.velocityX);
  ctx.translate(sx, sy);
  ctx.rotate(ang + Math.PI / 2);                       // point the tip forward
  ctx.fillStyle = proj.isCrit ? '#ff3366' : '#e0416b';
  ctx.beginPath();
  ctx.moveTo(0, s * 0.75);
  ctx.bezierCurveTo(-s, s * 0.05, -s * 0.6, -s * 0.78, 0, -s * 0.2);
  ctx.bezierCurveTo(s * 0.6, -s * 0.78, s, s * 0.05, 0, s * 0.75);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';           // the "break"
  ctx.lineWidth = Math.max(1, s * 0.14);
  ctx.beginPath();
  ctx.moveTo(0, -s * 0.35); ctx.lineTo(s * 0.15, -s * 0.05); ctx.lineTo(-s * 0.12, s * 0.2); ctx.lineTo(s * 0.05, s * 0.5); ctx.stroke();
}

/** Tactical Crossbow — a visible bolt: wood shaft, steel arrowhead, red
 *  fletching, oriented along velocity. Was a generic gold pistol bullet. */
function drawCrossbowBolt(ctx: CanvasRenderingContext2D, proj: ProjectileForVfx, sx: number, sy: number, zoom: number): void {
  const ang = Math.atan2(proj.velocityY, proj.velocityX);
  const L = Math.max(12, proj.size * zoom * 3.2);
  ctx.translate(sx, sy);
  ctx.rotate(ang);
  ctx.strokeStyle = '#6b4a2a';                         // wood shaft
  ctx.lineWidth = Math.max(1.5, proj.size * zoom * 0.5);
  ctx.beginPath(); ctx.moveTo(-L * 0.5, 0); ctx.lineTo(L * 0.35, 0); ctx.stroke();
  ctx.fillStyle = proj.isCrit ? '#ffffff' : '#c8ccd2'; // steel arrowhead
  ctx.beginPath(); ctx.moveTo(L * 0.5, 0); ctx.lineTo(L * 0.26, -L * 0.14); ctx.lineTo(L * 0.26, L * 0.14); ctx.closePath(); ctx.fill();
  ctx.fillStyle = '#b23b3b';                            // fletching
  ctx.beginPath(); ctx.moveTo(-L * 0.5, 0); ctx.lineTo(-L * 0.64, -L * 0.13); ctx.lineTo(-L * 0.4, 0); ctx.lineTo(-L * 0.64, L * 0.13); ctx.closePath(); ctx.fill();
}

export const WEAPON_VFX: Record<string, WeaponVfxSpec> = {
  // BETA 22.72 — the cone IS the weapon (def.flameCone); no projectile body.
  // BETA 22.74 — burn is REAL now (Dylan approved 2026-07-02): 2s @ 6 dmg/s,
  // halved vs elites/bosses. Damage ticks through the shared enemy burn
  // engine (0.5s cadence), attributed to this weapon via enemy.burnSource.
  fireball_wand: {
    burnVisualSec: 2.0,
    burnDps: 6,
    hitDecal: { kind: 'scorch', chance: 0.3, size: [22, 38] },
  },
  // BETA 22.73 — trail identities migrated from hardcoded id lists in the
  // weaponSystem trail renderer (rendering unchanged, just data-driven now).
  revolver:          { trailKind: 'streakCore' },  // gold tracer + white-hot core
  wpn_shitty_cannon: { trailKind: 'ghostHeavy' },  // dense cannonball smear

  // ── BETA 22.76 — per-weapon identity fills (22.75 audit closed the VFX gaps
  // where weapons were still on generic recolored bullets / shared circle hits).
  // .50 Cal Sniper — white-hot tracer streak (was generic fading circles, a
  // CORE-LAW "no plain circles" violation) + a bullet gouge on the floor.
  peanut_rifle:      { trailKind: 'streakCore', hitDecal: { kind: 'scorch', chance: 0.4, size: [9, 16] } },
  // The Intern (SMG) — fast thin tracer streak so it doesn't read as a slow
  // pistol bullet. (Casings/smoke come from spawnFireSignature below.)
  wpn_smg:           { trailKind: 'streak' },
  // RPG-7 — oriented rocket body + heavy smoke smear + big scorch crater.
  wpn_rpg:           { drawBody: drawRpgRocket, trailKind: 'ghostHeavy', hitDecal: { kind: 'scorch', chance: 0.5, size: [28, 46] } },
  // Sawed-Off — hot lead pellet body (dark rim + gold hotspot).
  blunderbuss:       { drawBody: drawBlunderbussPellet },
  // The Ex Girlfriend — cracked-heart projectile (Dylan: NOT pink bullets).
  wpn_ex_girlfriend: { drawBody: drawBrokenHeart },
  // Tactical Crossbow — a real bolt (shaft + steel head + fletching).
  wpn_crossbow:      { drawBody: drawCrossbowBolt },
  // Brain Freeze — thin ice line + an ice-crack frost patch on the floor.
  wpn_frozen_darts:  { trailKind: 'streak', hitDecal: { kind: 'frost', chance: 0.5, size: [16, 26] } },
  // Toilet Seat Boomerang — nasty dirty-water splat where it hits.
  wpn_the_terminator: { hitDecal: { kind: 'splat', chance: 0.35, size: [18, 30] } },
  // Dodge This — rubber scuff smear on the floor (squash/stretch is deferred
  // polish; this gives the aftermath identity now).
  tactical_basketball: { hitDecal: { kind: 'smear', chance: 0.4, size: [18, 30] } },
  // Viking Axe — a bloody chop mark where the blade bites.
  viking_axe:        { hitDecal: { kind: 'splat', chance: 0.4, size: [14, 24] } },

  // BETA 22.74 — per-weapon melee contact sparks (identity law: the shared
  // gray violated "every effect unique"). Full per-weapon melee VFX passes
  // come with the 17-weapon rollout; this fixes the color identity now.
  spatula_slash:     { meleeSparkColor: '#b71c1c' }, // Barbed Wire Bat — blood flecks off the wire
  wpn_chain_whip:    { meleeSparkColor: '#ffe082' }, // Chain Whip — metal clang, hot yellow-white
  wpn_dracs_fang:    { meleeSparkColor: '#ff9d45' }, // Homemade Chainsaw — hot grinding-metal sparks (22.74 rider: audit covered bat+whip, chainsaw was left on gray)
};

export function getWeaponVfx(weaponId: string): WeaponVfxSpec | undefined {
  return WEAPON_VFX[weaponId];
}
